/**
 * The Microsoft Store listing, assembled from the shared metadata and shaped
 * the way the Partner Center submission API expects.
 *
 * `buildStoreListing()` produces exactly the `baseListing` fields the release
 * workflow writes onto every language of the pending submission, so the Store
 * listing is generated from this repository on every release instead of being
 * typed into Partner Center by hand.
 *
 * Partner Center field limits are enforced here, by
 * `assertStoreListingLimits()`, so an over-long description fails in CI (and in
 * `pnpm store:listing`) rather than at submission time.
 */
import { renderStoreDescription, shortStore } from './descriptions.js';
import { storeFeatures } from './features.js';
import { product, urls } from './identity.js';
/** Search terms: 7 fields of 30 characters, not shown to users, Store search only. */
export const searchTerms = [
    'fleet management',
    'transport',
    'logistics',
    'trucking',
    'trip management',
    'freight',
    'vehicle tracking',
];
/**
 * Copyright and trademark line, as Partner Center shows it on the listing page.
 * The year is the first year of publication and does not move on its own; bump
 * it deliberately if the notice should read as a range.
 */
export const copyright = `© 2026 ${product.publisher}. All rights reserved. ${product.name} is a trademark of ${product.publisher}.`;
/**
 * Used where the full product name does not fit (tiles and some Store
 * surfaces). Partner Center falls back to the product name when it is empty,
 * and the name is already short, so this is the name itself.
 */
export const shortTitle = product.name;
/** Partner Center's documented limits for the fields this repository writes. */
export const STORE_LIMITS = {
    /** "What's new in this version". */
    releaseNotes: 1500,
    /** Description. */
    description: 10000,
    /** Short description. */
    shortDescription: 1000,
    /** Product features: at most 20 entries of at most 200 characters. */
    featureCount: 20,
    featureLength: 200,
    /** Search terms: at most 7 entries of at most 30 characters. */
    searchTermCount: 7,
    searchTermLength: 30,
    shortTitle: 50,
    copyright: 200,
};
/**
 * Builds the listing. `releaseNotes` comes from the desktop app's release notes
 * (apps/desktop/src/shared/constants/releases.ts) at release time; omit it to
 * generate the listing without touching the "What's new" field.
 */
export function buildStoreListing(releaseNotes) {
    const listing = {
        description: renderStoreDescription(),
        shortDescription: shortStore,
        features: storeFeatures,
        keywords: searchTerms,
        shortTitle,
        copyrightAndTrademarkInfo: copyright,
        privacyPolicy: urls.privacy,
        websiteUrl: urls.website,
        supportContact: urls.support,
    };
    if (releaseNotes !== undefined)
        listing.releaseNotes = releaseNotes;
    assertStoreListingLimits(listing);
    return listing;
}
/**
 * Throws with every violation at once, so one run reports all the edits needed
 * rather than one per attempt.
 */
export function assertStoreListingLimits(listing) {
    const problems = [];
    const tooLong = (label, value, limit) => {
        if (value.length > limit) {
            problems.push(`${label} is ${value.length} characters; the limit is ${limit}.`);
        }
    };
    tooLong('description', listing.description, STORE_LIMITS.description);
    tooLong('shortDescription', listing.shortDescription, STORE_LIMITS.shortDescription);
    tooLong('shortTitle', listing.shortTitle, STORE_LIMITS.shortTitle);
    tooLong('copyrightAndTrademarkInfo', listing.copyrightAndTrademarkInfo, STORE_LIMITS.copyright);
    if (listing.releaseNotes !== undefined) {
        tooLong('releaseNotes', listing.releaseNotes, STORE_LIMITS.releaseNotes);
    }
    if (listing.features.length > STORE_LIMITS.featureCount) {
        problems.push(`there are ${listing.features.length} product features; the limit is ${STORE_LIMITS.featureCount}.`);
    }
    listing.features.forEach((feature, index) => {
        tooLong(`product feature ${index + 1}`, feature, STORE_LIMITS.featureLength);
    });
    if (listing.keywords.length > STORE_LIMITS.searchTermCount) {
        problems.push(`there are ${listing.keywords.length} search terms; the limit is ${STORE_LIMITS.searchTermCount}.`);
    }
    listing.keywords.forEach((keyword, index) => {
        tooLong(`search term ${index + 1}`, keyword, STORE_LIMITS.searchTermLength);
    });
    if (problems.length > 0) {
        throw new Error(`The Microsoft Store listing exceeds Partner Center's limits:\n  ${problems.join('\n  ')}`);
    }
}
//# sourceMappingURL=store.js.map